// store/subscriptionsStore.ts
import { create } from "zustand";
import { BillingSubscription } from "../../../types/subscription";
import { getAllSubscriptions } from "../services";


interface SubscriptionStats {
  totalRevenue: number;
  activeSubscribers: number;
  subscriptions: BillingSubscription[];
  recentSubscriptions: BillingSubscription[];
}

interface SubscriptionsState {
  subscriptions: BillingSubscription[];
  stats: SubscriptionStats | null;
  loading: boolean;
  fetchSubscriptions: () => Promise<void>;
}

export const useSubscriptionsStore = create<SubscriptionsState>((set) => ({
  subscriptions: [],
  stats: null,
  loading: false,

  fetchSubscriptions: async () => {
    set({ loading: true });
    try {
      const subscriptions = await getAllSubscriptions();

      const now = Date.now();
      const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000; // 30 days in ms

      const stats = subscriptions.reduce(
        (acc, subscription) => {
          if (subscription.status === "paid") {
            acc.subscriptions.push(subscription);

            const price = parseFloat(subscription.price.replace(/[^0-9.-]+/g, ""));
            acc.totalRevenue += price;

            if (!acc.activeSubscriberIds.has(subscription.userid)) {
              acc.activeSubscriberIds.add(subscription.userid);
            }

            // Check if created within the past 30 days
            const createdTime = new Date(subscription.created_at).getTime();
            if (!isNaN(createdTime)) {
              if (createdTime >= thirtyDaysAgo) {
                acc.recentSubscriptions.push(subscription);
              }
            } else {
              console.warn("Invalid created_at for subscription:", subscription._id, subscription.created_at);
            }
          }
          return acc;
        },
        {
          totalRevenue: 0,
          activeSubscriberIds: new Set<string>(),
          subscriptions: [] as BillingSubscription[],
          recentSubscriptions: [] as BillingSubscription[],
        }
      );

      set({
        subscriptions,
        stats: {
          totalRevenue: stats.totalRevenue,
          activeSubscribers: stats.activeSubscriberIds.size,
          subscriptions: stats.subscriptions,
          recentSubscriptions: stats.recentSubscriptions,
        },
        loading: false,
      });
    } catch (error) {
      console.error("Failed to fetch subscriptions", error);
      set({ loading: false });
    }
  },
}));
